import { Link, useParams } from 'react-router-dom'
import { useService } from '@/lib/cluster'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusBadge } from '@/components/StatusBadge'
import { HealthCheckPanel } from '@/components/HealthCheckPanel'
import { useServiceMetrics } from '@/lib/metrics'
import { MetricLine } from '@/components/charts/MetricLine'
import { bytes, pct } from '@/lib/format'

export function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useService(id)
  const metrics = useServiceMetrics(id)

  return (
    <div className="space-y-6">
      <div>
        <Link to="/services" className="text-sm text-muted-foreground hover:underline">
          ← Back to services
        </Link>
        <h1 className="text-3xl font-bold tracking-tight">{data?.name ?? id}</h1>
      </div>

      {isLoading && <Skeleton className="h-40" />}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Identity</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div>Docker ID: <span className="text-muted-foreground">{data.docker_id}</span></div>
                <div>Image: <span className="text-muted-foreground">{data.image ?? '—'}</span></div>
                <div>Status: <StatusBadge status={data.status} /></div>
                <div>Type: <span className="text-muted-foreground">
                  {data.classification_override ?? data.classification_auto ?? 'unknown'}
                </span></div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Labels</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                  {JSON.stringify(data.labels, null, 2)}
                </pre>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Container metrics · last hour</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                {metrics.isLoading && <Skeleton className="h-48" />}
                {metrics.isError && <p>Metrics could not be loaded.</p>}
                {!metrics.isLoading && !metrics.isError && !metrics.data?.points.length && <p>No container samples in the last hour.</p>}
                {!!metrics.data?.points.length && <div className="grid gap-4 md:grid-cols-2">
                  <div>Average container CPU
                    <MetricLine data={metrics.data.points as unknown as Record<string, unknown>[]} xKey="b" yKey="cpu_pct" format={(v) => pct(v ?? null, 1)} />
                  </div>
                  <div>Average container memory
                    <MetricLine data={metrics.data.points as unknown as Record<string, unknown>[]} xKey="b" yKey="mem_used" format={(v) => bytes(v ?? null)} />
                  </div>
                </div>}
              </CardContent>
            </Card>
          </div>
          {id && <HealthCheckPanel serviceId={id} />}
        </>
      )}
    </div>
  )
}
